// frontend/src/utils/fileUtils.js

/**
 * Sanitizes a filename by removing or replacing problematic characters
 * that can cause issues in various operating systems and backend services
 *
 * @param {string} filename - The original filename to sanitize
 * @returns {string} - The sanitized filename safe for use across systems
 *
 * @example
 * sanitizeFilename("Re: Yengo nomination_Naomi Wray.pdf")
 * // Returns: "Re_Yengo_nomination_Naomi_Wray.pdf"
 */
export const sanitizeFilename = (filename) => {
  if (typeof filename !== 'string' || !filename.trim()) {
    return '';
  }

  return filename
    .replace(/[<>:"|?*/\\]/g, '_') // Replace unsafe characters with underscore
    .replace(/\s+/g, '_') // Replace whitespace with underscore
    .trim() // Remove leading/trailing spaces
    .replace(/_+/g, '_') // Collapse multiple underscores into single underscore
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
};

/**
 * Creates a new File object with a sanitized filename
 *
 * @param {File} file - The original file object
 * @returns {File} - New file object with sanitized name
 */
export const createSanitizedFile = (file) => {
  if (!(file instanceof File)) {
    throw new Error('Input must be a File object');
  }

  const sanitizedName = sanitizeFilename(file.name);
  return new File([file], sanitizedName, { type: file.type });
};
